import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Stethoscope, Users, Wrench, Clock, ClipboardList, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_META = {
  physician: { label: "Physician", icon: Stethoscope, color: "#0B7E8C" },
  nurse: { label: "Nurse", icon: Users, color: "#8B5CF6" },
  technician: { label: "Technician", icon: Wrench, color: "#F59E0B" },
  physiotherapist: { label: "Physiotherapist", icon: Activity, color: "#0EA5E9" },
};
const STATUS_META = {
  pending: { label: "Pending", color: "#B45309", bg: "#FEF3C7" },
  planned: { label: "Planned", color: "#1D4ED8", bg: "#DBEAFE" },
  assigned: { label: "Assigned", color: "#065F46", bg: "#D1FAE5" },
  completed: { label: "Completed", color: "#374151", bg: "#F1F5F9" },
  cancelled: { label: "Cancelled", color: "#991B1B", bg: "#FEE2E2" },
};
const WRITE_ROLES = ["admin", "hah_coordinator", "care_coordinator", "clinical_director"];

export default function ServiceOrdersTab({ patientId, readOnly }) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role_code) && !readOnly;
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [priority, setPriority] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/patients/${patientId}/service-orders`); setOrders(r.data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canWrite) api.get("/service-catalog", { params: { active: true } }).then((r) => setServices(r.data.items || [])).catch(() => {}); }, [canWrite]);

  function toggleCode(c) {
    setCodes((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  }

  async function add() {
    if (codes.length === 0) { toast.error("Select at least one service"); return; }
    setSaving(true);
    try {
      for (const c of codes) {
        await api.post(`/patients/${patientId}/service-orders`, { service_code: c, priority: priority || null });
      }
      toast.success(`${codes.length} service order(s) added`); setCodes([]); setPriority(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  async function del(id) {
    try { await api.delete(`/service-orders/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="hah-card p-5" data-testid="cl-service-orders">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[15px] font-semibold text-[#1F2937] flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#0B7E8C]" /> Service Orders
        </h3>
        <span className="text-[12px] text-[#94A3B8]">Requested services routed by the Dispatch Orchestrator</span>
      </div>

      {canWrite && (
        <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[11px] font-medium text-[#374151] mb-1">Services</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="w-full h-10 flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cl-so-service">
                  <span className={codes.length ? "text-[#1F2937]" : "text-muted-foreground"}>
                    {codes.length === 0 ? "Select service(s)" : `${codes.length} service(s) selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" data-testid="cl-so-service-list">
                <div className="max-h-60 overflow-auto py-1">
                  {services.map((s) => (
                    <label key={s.code} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer" data-testid={`cl-so-opt-${s.code}`}>
                      <input type="checkbox" checked={codes.includes(s.code)} onChange={() => toggleCode(s.code)} className="rounded" />
                      <span className="flex-1 text-[13px] text-[#1F2937]">{s.name}</span>
                      <span className="text-[11px] text-[#94A3B8]">{s.est_duration_min || "?"} min</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="w-40">
            <label className="block text-[11px] font-medium text-[#374151] mb-1">Priority</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="cl-so-priority"><SelectValue placeholder="Service default" /></SelectTrigger>
              <SelectContent>{["routine", "urgent", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-so-add">
            <Plus className="w-4 h-4 mr-1.5" /> {codes.length > 1 ? `Add ${codes.length}` : "Add"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#6B7280] py-6 text-center">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="text-sm text-[#94A3B8] italic py-6 text-center" data-testid="cl-so-empty">No service orders for this patient.</div>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => {
            const st = STATUS_META[o.status] || STATUS_META.pending;
            return (
              <div key={o.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#F1F5F9]" data-testid={`cl-so-row-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium text-[#1F2937]">{o.service_name || o.service_code}</div>
                  <div className="text-[12px] text-[#6B7280] flex items-center gap-2 flex-wrap mt-0.5">
                    {(o.required_roles || []).map((r, i) => { const m = ROLE_META[r.role] || ROLE_META.nurse; const I = m.icon; return (
                      <span key={i} className="inline-flex items-center gap-1" style={{ color: m.color }}><I className="w-3 h-3" />{r.count > 1 ? `${r.count}×` : ""}{m.label}</span>
                    ); })}
                    {o.est_duration_min && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{o.est_duration_min} min</span>}
                    <span className="capitalize">· {o.priority}</span>
                  </div>
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                {canWrite && o.status === "pending" && (
                  <button onClick={() => del(o.id)} className="text-[#EF4444] p-1 shrink-0" data-testid={`cl-so-del-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
